import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const fmtRs = (n) => (n === null || n === undefined || n === '') ? '—' : `₹${Number(n).toFixed(2)}`;

export const nowLocalInput = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const fmtDateTime = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? String(s) : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

/* Revision entry form: capture revised cost + date/time + revised-by + reason. */
export function CostRevisionForm({ title, processName, previousCost, defaultBy, onSubmit, onClose }) {
  const [revisedCost, setRevisedCost] = useState(previousCost !== '' && previousCost != null ? String(previousCost) : '');
  const [when, setWhen] = useState(nowLocalInput());
  const [by, setBy] = useState(defaultBy || '');
  const [reason, setReason] = useState('');

  const submit = () => {
    if (revisedCost === '' || isNaN(Number(revisedCost))) { toast.error('Enter a revised cost'); return; }
    if (!by.trim()) { toast.error('Enter who revised it'); return; }
    onSubmit({
      process_name: processName || undefined,
      previous_cost: Number(previousCost) || 0,
      revised_cost: Number(revisedCost),
      revised_at: when ? new Date(when).toISOString() : new Date().toISOString(),
      revised_by: by.trim(),
      reason: reason.trim(),
    });
  };

  const roBox = 'w-full border border-slate-200 bg-slate-50 rounded-sm h-9 px-3 text-sm flex items-center text-slate-700';
  const inp = 'w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900';
  const lbl = 'text-xs font-bold uppercase text-slate-500 mb-1 block';

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="cost-revision-form">
        <DialogHeader><DialogTitle className="font-heading text-slate-900">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-1">
          {processName && (
            <div>
              <label className={lbl}>Process Name</label>
              <div className={`${roBox} font-mono`}>{processName}</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Previous Cost (₹)</label>
              <div className={`${roBox} font-mono`}>{previousCost !== '' && previousCost != null ? fmtRs(previousCost) : '—'}</div>
            </div>
            <div>
              <label className={lbl}>Revised Cost (₹) *</label>
              <input type="number" step="0.01" value={revisedCost} onChange={e => setRevisedCost(e.target.value)}
                data-testid="revision-cost" className={`${inp} font-mono`} autoFocus />
            </div>
          </div>
          <div>
            <label className={lbl}>Revision Date &amp; Time *</label>
            <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)}
              data-testid="revision-datetime" className={`${inp} font-mono`} />
          </div>
          <div>
            <label className={lbl}>Revised By *</label>
            <input type="text" value={by} onChange={e => setBy(e.target.value)} placeholder="Your name"
              data-testid="revision-by" className={inp} />
          </div>
          <div>
            <label className={lbl}>Reason <span className="text-slate-400 font-normal normal-case">(optional)</span></label>
            <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Vendor rate change"
              data-testid="revision-reason" className={inp} />
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={submit} data-testid="revision-save"
              className="flex-1 h-9 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800">Save Revision</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* Revision history viewer. */
export function CostRevisionHistory({ title, records, showName, onClose }) {
  const sorted = [...(records || [])].sort((a, b) => new Date(b.revised_at || 0) - new Date(a.revised_at || 0));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cost-revision-history">
        <DialogHeader><DialogTitle className="font-heading text-slate-900">{title}</DialogTitle></DialogHeader>
        {sorted.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No revisions recorded yet.</p>
        ) : (
          <div className="overflow-x-auto mt-1">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                  {showName && <th className="text-left px-3 py-2 font-semibold">Process</th>}
                  <th className="text-right px-3 py-2 font-semibold">Previous</th>
                  <th className="text-right px-3 py-2 font-semibold">Revised</th>
                  <th className="text-right px-3 py-2 font-semibold">Change</th>
                  <th className="text-left px-3 py-2 font-semibold">Date &amp; Time</th>
                  <th className="text-left px-3 py-2 font-semibold">Revised By</th>
                  <th className="text-left px-3 py-2 font-semibold">Reason</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => {
                  const delta = (Number(r.revised_cost) || 0) - (Number(r.previous_cost) || 0);
                  return (
                    <tr key={i} className="border-b border-slate-100">
                      {showName && <td className="px-3 py-2 font-mono text-slate-700">{r.process_name || '—'}</td>}
                      <td className="px-3 py-2 text-right font-mono text-slate-500">{fmtRs(r.previous_cost)}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-slate-800">{fmtRs(r.revised_cost)}</td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${delta > 0 ? 'text-red-600' : delta < 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {delta > 0 ? '+' : ''}{fmtRs(delta)}
                      </td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtDateTime(r.revised_at)}</td>
                      <td className="px-3 py-2 text-slate-600">{r.revised_by || '—'}</td>
                      <td className="px-3 py-2 text-slate-500">{r.reason || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
